import dayjs from "dayjs";
import {
	ArrowRight,
	Briefcase,
	Calendar,
	Check,
	CheckCircle2,
	CircleDollarSign,
	Copy,
	Crown,
	Mail,
	MapPin,
	MoreVertical,
	Pencil,
	Plus,
	RefreshCw,
	Sparkles,
	Trash2,
	X,
} from "lucide-react";
import React from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";

import { Button } from "@/app/components/ui/button";
import {
	useExtendClaimCompanyJobDeadlineMutation,
	useMarkClaimCompanyJobFilledMutation,
} from "@/app/services/create-resume/queries";

import { useCompanyDashboard } from "./CompanyDashboardProvider";
import { ContentSection, FilterBar } from "./shared";

function Modal({ title, children, onClose, width = "max-w-xl" }) {
	return (
		<div className="modal-backdrop" onClick={onClose}>
			<div className={`modal-content ${width}`} onClick={(event) => event.stopPropagation()}>
				<div className="-mx-6 mb-5 flex items-center justify-between border-b border-border px-6 pb-4">
					<h2 className="text-lg font-bold text-foreground">{title}</h2>
					<button type="button" onClick={onClose} className="rounded-lg p-1.5 hover:bg-secondary">
						<X className="h-4 w-4" />
					</button>
				</div>
				{children}
			</div>
		</div>
	);
}

function JobsLoadingState() {
	return (
		<div className="rounded-lg border border-border bg-white py-12 text-center">
			<RefreshCw className="mx-auto mb-3 h-6 w-6 animate-spin text-primary-700" />
			<h3 className="mb-1 font-semibold text-foreground">Loading jobs</h3>
			<p className="text-sm text-muted-foreground">Fetching the latest company roles from the backend.</p>
		</div>
	);
}

function JobsErrorState({ onRetry }) {
	return (
		<div className="rounded-lg border border-red-100 bg-white py-10 text-center">
			<div className="mx-auto mb-3 flex h-12 w-12 items-center justify-center rounded-lg bg-red-50">
				<Briefcase className="h-6 w-6 text-red-600" />
			</div>
			<h3 className="mb-1 font-semibold text-foreground">Could not load jobs</h3>
			<p className="mb-4 text-sm text-muted-foreground">Failed to load jobs from the backend. Try again.</p>
			<Button onClick={onRetry}>
				<RefreshCw className="mr-2 h-4 w-4" />
				Retry
			</Button>
		</div>
	);
}

function JobsEmptyState({ icon, title, description, action }) {
	return (
		<div className="rounded-lg border-2 border-dashed border-border bg-white py-8 text-center">
			<div className="mx-auto mb-3 flex h-12 w-12 items-center justify-center rounded-lg bg-secondary">{icon}</div>
			<h3 className="mb-1 font-semibold text-foreground">{title}</h3>
			<p className="mb-3 text-sm text-muted-foreground">{description}</p>
			{action}
		</div>
	);
}

function getErrorMessage(error, fallback) {
	return error?.response?.data?.message || fallback;
}

function toDayMonthYear(dateValue) {
	const parsed = dayjs(dateValue);
	if (!parsed.isValid()) return null;
	return {
		day: Number(parsed.format("DD")),
		month: Number(parsed.format("MM")),
		year: Number(parsed.format("YYYY")),
	};
}

export default function JobsTab() {
	const { companyId, companyName, branding, jobs, plan, jobsQuery, goToUpgrade } = useCompanyDashboard();
	const extendDeadlineMutation = useExtendClaimCompanyJobDeadlineMutation(companyId);
	const markJobFilledMutation = useMarkClaimCompanyJobFilledMutation(companyId);

	const [searchQuery, setSearchQuery] = React.useState("");
	const [typeFilter, setTypeFilter] = React.useState("all");
	const [levelFilter, setLevelFilter] = React.useState("all");
	const [activeRoleTab, setActiveRoleTab] = React.useState("open");
	const [activeMenu, setActiveMenu] = React.useState(null);
	const [showJobInstructionsModal, setShowJobInstructionsModal] = React.useState(false);
	const [jobToExtend, setJobToExtend] = React.useState(null);
	const [jobToMarkFilled, setJobToMarkFilled] = React.useState(null);
	const [actionInProgressJobId, setActionInProgressJobId] = React.useState(null);

	const isSubmittingJob = extendDeadlineMutation.isPending || markJobFilledMutation.isPending;

	const filteredJobs = React.useMemo(() => {
		const query = searchQuery.trim().toLowerCase();
		return jobs.filter((job) => {
			const matchesSearch =
				!query ||
				job.title.toLowerCase().includes(query) ||
				job.location.toLowerCase().includes(query) ||
				job.company?.toLowerCase().includes(query);
			const matchesType = typeFilter === "all" || job.type === typeFilter;
			const matchesLevel = levelFilter === "all" || job.level === levelFilter;
			return matchesSearch && matchesType && matchesLevel;
		});
	}, [jobs, levelFilter, searchQuery, typeFilter]);

	const openJobs = filteredJobs.filter((job) => job.status === "open");
	const closedJobs = filteredJobs.filter((job) => job.status === "filled");
	const hasActiveFilters = Boolean(searchQuery || typeFilter !== "all" || levelFilter !== "all");
	const totalOpenJobs = jobs.filter((job) => job.status === "open").length;
	const totalClosedJobs = jobs.filter((job) => job.status === "filled").length;

	const handleExtendDeadline = React.useCallback(
		async (job, newDeadline) => {
			if (!job) return false;
			const deadlineDate = toDayMonthYear(newDeadline);
			if (!newDeadline || !deadlineDate) {
				toast.error("Please provide a valid new deadline.");
				return false;
			}

			try {
				setActionInProgressJobId(job.id);
				await extendDeadlineMutation.mutateAsync({ id: job.id, deadlineDate });
				await jobsQuery.refetch();
				toast.success("Deadline updated successfully.");
				setJobToExtend(null);
				return true;
			} catch (error) {
				toast.error(getErrorMessage(error, "Failed to update deadline. Please try again."));
				return false;
			} finally {
				setActionInProgressJobId(null);
			}
		},
		[extendDeadlineMutation, jobsQuery],
	);

	const handleMarkAsFilled = React.useCallback(
		async (job) => {
			if (!job?.id) return false;

			try {
				setActionInProgressJobId(job.id);
				await markJobFilledMutation.mutateAsync({ id: job.id });
				await jobsQuery.refetch();
				toast.success("Job marked as filled.");
				setJobToMarkFilled(null);
				return true;
			} catch (error) {
				toast.error(getErrorMessage(error, "Failed to mark job as filled. Please try again."));
				return false;
			} finally {
				setActionInProgressJobId(null);
			}
		},
		[jobsQuery, markJobFilledMutation],
	);

	return (
		<ContentSection companyHeader>
			<div className="mx-auto max-w-4xl space-y-4">
				{plan === "starter" ? (
					<div className="relative overflow-hidden rounded-lg bg-primary-50 p-4 text-foreground">
						<div className="absolute right-0 top-0 h-40 w-40 -translate-y-1/2 translate-x-1/2 rounded-full bg-primary-100" />
						<div className="absolute bottom-0 left-0 h-32 w-32 translate-y-1/2 -translate-x-1/2 rounded-full bg-primary-100" />
						<div className="relative flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
							<div>
								<div className="mb-1 flex items-center gap-2">
									<Crown className="h-5 w-5 text-amber-400" />
									<h3 className="text-lg font-bold text-foreground">Unlock More Features</h3>
								</div>
								<p className="max-w-md text-sm text-muted-foreground">
									Upgrade to Corporate to get custom subdomains, embed widgets, advanced analytics, and more.
								</p>
							</div>
							<Button
								variant="secondary"
								className="shrink-0 border border-primary-200 bg-white text-primary-700 hover:bg-primary-100"
								onClick={goToUpgrade}
							>
								<Sparkles className="h-4 w-4" />
								Upgrade to Corporate
							</Button>
						</div>
					</div>
				) : null}

				<div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
					<div>
						<h1 className="text-xl font-bold text-foreground">Your Jobs</h1>
						<p className="text-sm text-muted-foreground">Manage and track all your job listings for {companyName}</p>
						{jobsQuery.isFetching && !jobsQuery.isLoading ? (
							<p className="mt-1 text-xs text-primary-700">Refreshing jobs from the backend...</p>
						) : null}
					</div>
					<Button onClick={() => setShowJobInstructionsModal(true)}>
						<Plus className="mr-2 h-4 w-4" />
						Add New Job
					</Button>
				</div>

				<div className="flex w-fit gap-0.5 rounded-md bg-secondary p-0.5">
					<button
						type="button"
						onClick={() => setActiveRoleTab("open")}
						className={`rounded px-3 py-1.5 text-sm font-medium transition-all ${
							activeRoleTab === "open"
								? "bg-white text-foreground shadow-xs"
								: "text-muted-foreground hover:text-foreground"
						}`}
					>
						Open Roles
						<span
							className={`ml-1.5 rounded px-1.5 py-0.5 text-xs ${
								activeRoleTab === "open" ? "bg-blue-100 text-primary-700" : "bg-secondary text-muted-foreground"
							}`}
						>
							{totalOpenJobs}
						</span>
					</button>
					<button
						type="button"
						onClick={() => setActiveRoleTab("closed")}
						className={`rounded px-3 py-1.5 text-sm font-medium transition-all ${
							activeRoleTab === "closed"
								? "bg-white text-foreground shadow-xs"
								: "text-muted-foreground hover:text-foreground"
						}`}
					>
						Closed Roles
						<span
							className={`ml-1.5 rounded px-1.5 py-0.5 text-xs ${
								activeRoleTab === "closed" ? "bg-secondary text-foreground" : "bg-secondary text-muted-foreground"
							}`}
						>
							{totalClosedJobs}
						</span>
					</button>
				</div>

				<FilterBar
					searchValue={searchQuery}
					onSearchChange={setSearchQuery}
					searchPlaceholder="Search by title, company, or location..."
					dropdowns={[
						{
							value: typeFilter,
							onChange: setTypeFilter,
							options: [
								{ value: "all", label: "All Types" },
								...Array.from(new Set(jobs.map((job) => job.type))).map((item) => ({ value: item, label: item })),
							],
						},
						{
							value: levelFilter,
							onChange: setLevelFilter,
							options: [
								{ value: "all", label: "All Levels" },
								...Array.from(new Set(jobs.map((job) => job.level))).map((item) => ({ value: item, label: item })),
							],
						},
					]}
					clearButton={
						hasActiveFilters
							? {
									show: true,
									onClick: () => {
										setSearchQuery("");
										setTypeFilter("all");
										setLevelFilter("all");
									},
								}
							: undefined
					}
				/>

				{jobsQuery.isLoading ? (
					<JobsLoadingState />
				) : jobsQuery.isError ? (
					<JobsErrorState onRetry={() => jobsQuery.refetch()} />
				) : activeRoleTab === "open" ? (
					<section>
						{openJobs.length === 0 ? (
							<JobsEmptyState
								icon={<Briefcase className="h-6 w-6 text-muted-foreground" />}
								title="No open roles"
								description={
									hasActiveFilters
										? "No jobs match your current filters"
										: "No open jobs are currently available from the backend"
								}
								action={
									!hasActiveFilters ? (
										<button
											type="button"
											onClick={() => setShowJobInstructionsModal(true)}
											className="inline-flex items-center gap-1.5 rounded-md bg-blue-50 px-3 py-1.5 text-sm font-medium text-primary-700 transition-colors hover:bg-blue-100"
										>
											<Plus className="h-4 w-4" />
											Add New Job
										</button>
									) : undefined
								}
							/>
						) : (
							<div>
								{openJobs.map((job) => {
									const isActionLoading = actionInProgressJobId === job.id;

									return (
										<div key={job.id} className="border-b border-border bg-white py-4 transition-all hover:bg-gray-50">
											<div className="flex items-center gap-4">
												<div className="flex h-12 w-12 shrink-0 items-center justify-center overflow-hidden rounded-lg bg-blue-50">
													{branding.companyLogo || job.logoUrl ? (
														<img
															src={branding.companyLogo || job.logoUrl}
															alt={`${companyName} logo`}
															className="h-full w-full object-cover"
														/>
													) : (
														<Briefcase className="h-5 w-5 text-primary-700" />
													)}
												</div>

												<div className="min-w-0 flex-1">
													<div className="mb-1 flex items-center gap-2">
														<span className="truncate font-semibold text-foreground">{job.title}</span>
													</div>
													<div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-sm text-muted-foreground">
														<span className="flex items-center gap-1">
															<MapPin className="h-3.5 w-3.5" />
															{job.location}
														</span>
														<span className="flex items-center gap-1">
															<Calendar className="h-3.5 w-3.5" />
															{job.deadline ? dayjs(job.deadline).format("MMM D, YYYY") : "No deadline"}
														</span>
														<span className="flex items-center gap-1">
															<CircleDollarSign className="h-3.5 w-3.5" />
															{job.salary || "Negotiable"}
														</span>
													</div>
												</div>

												<div className="relative shrink-0">
													<button
														type="button"
														disabled={isActionLoading}
														onClick={() => setActiveMenu((current) => (current === job.id ? null : job.id))}
														className="rounded p-1.5 transition-colors hover:bg-secondary disabled:opacity-50"
													>
														<MoreVertical className="h-4 w-4 text-muted-foreground" />
													</button>

													{activeMenu === job.id ? (
														<>
															<div className="fixed inset-0 z-10" onClick={() => setActiveMenu(null)} />
															<div className="absolute right-0 top-full z-20 mt-1 w-52 rounded-lg border border-border bg-white py-1 shadow-lg">
																<button
																	type="button"
																	disabled
																	className="flex w-full cursor-not-allowed items-center gap-2 px-3 py-2 text-sm text-muted-foreground disabled:opacity-100"
																>
																	<Pencil className="h-3.5 w-3.5" />
																	Edit Job
																</button>
																<button
																	type="button"
																	disabled={isSubmittingJob}
																	onClick={() => {
																		setJobToExtend(job);
																		setActiveMenu(null);
																	}}
																	className="flex w-full items-center gap-2 px-3 py-2 text-sm text-foreground transition-colors hover:bg-secondary disabled:opacity-50"
																>
																	<Calendar className="h-3.5 w-3.5" />
																	Extend Deadline
																</button>
																<button
																	type="button"
																	disabled={isSubmittingJob || isActionLoading}
																	onClick={() => {
																		setJobToMarkFilled(job);
																		setActiveMenu(null);
																	}}
																	className="flex w-full items-center gap-2 px-3 py-2 text-sm text-foreground transition-colors hover:bg-secondary disabled:opacity-50"
																>
																	<CheckCircle2 className="h-3.5 w-3.5" />
																	Mark as Filled
																</button>
																<button
																	type="button"
																	disabled
																	className="flex w-full cursor-not-allowed items-center gap-2 px-3 py-2 text-sm text-muted-foreground disabled:opacity-100"
																>
																	<RefreshCw className="h-3.5 w-3.5" />
																	<span>Refresh Freshness</span>
																	<Sparkles className="ml-auto h-3 w-3" />
																</button>
																<div className="my-1 border-t border-border" />
																<button
																	type="button"
																	disabled
																	className="flex w-full cursor-not-allowed items-center gap-2 px-3 py-2 text-sm text-muted-foreground disabled:opacity-100"
																>
																	<Trash2 className="h-3.5 w-3.5" />
																	Delete Job
																</button>
															</div>
														</>
													) : null}
												</div>
											</div>
										</div>
									);
								})}
							</div>
						)}
					</section>
				) : (
					<section>
						{closedJobs.length === 0 ? (
							<JobsEmptyState
								icon={<CheckCircle2 className="h-6 w-6 text-muted-foreground" />}
								title="No closed roles"
								description={
									hasActiveFilters
										? "No closed jobs match your current filters"
										: "Roles past deadline or marked as filled will appear here"
								}
							/>
						) : (
							<div>
								{closedJobs.map((job) => (
									<div key={job.id} className="border-b border-border bg-white/60 py-4">
										<div className="flex items-center gap-4">
											<div className="flex h-12 w-12 shrink-0 items-center justify-center overflow-hidden rounded-lg bg-secondary opacity-60">
												{branding.companyLogo || job.logoUrl ? (
													<img
														src={branding.companyLogo || job.logoUrl}
														alt={`${companyName} logo`}
														className="h-full w-full object-cover grayscale"
													/>
												) : (
													<Briefcase className="h-5 w-5 text-muted-foreground" />
												)}
											</div>

											<div className="min-w-0 flex-1">
												<div className="mb-1 flex items-center gap-2">
													<span className="truncate font-semibold text-muted-foreground">{job.title}</span>
													<span className="flex shrink-0 items-center gap-0.5 rounded-md bg-[#dff7ea] px-2 py-1 text-xs font-medium text-[#017336]">
														<CheckCircle2 className="h-3 w-3" />
														Filled
													</span>
												</div>
												<div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-sm text-muted-foreground">
													<span className="flex items-center gap-1">
														<MapPin className="h-3.5 w-3.5" />
														{job.location}
													</span>
													<span className="flex items-center gap-1">
														<CheckCircle2 className="h-3.5 w-3.5 text-green-500" />
														Closed {job.closedDate || "recently"}
													</span>
													<span className="flex items-center gap-1">
														<CircleDollarSign className="h-3.5 w-3.5" />
														{job.salary || "Negotiable"}
													</span>
												</div>
											</div>
										</div>
									</div>
								))}
							</div>
						)}
					</section>
				)}
			</div>

			{showJobInstructionsModal ? (
				<JobSubmissionInstructionsModal onClose={() => setShowJobInstructionsModal(false)} />
			) : null}

			{jobToExtend ? (
				<ExtendDeadlineModal
					job={jobToExtend}
					isSubmitting={isSubmittingJob || actionInProgressJobId === jobToExtend.id}
					onClose={() => setJobToExtend(null)}
					onSubmit={handleExtendDeadline}
				/>
			) : null}

			{jobToMarkFilled ? (
				<MarkJobFilledModal
					job={jobToMarkFilled}
					isSubmitting={isSubmittingJob || actionInProgressJobId === jobToMarkFilled.id}
					onClose={() => setJobToMarkFilled(null)}
					onConfirm={handleMarkAsFilled}
				/>
			) : null}
		</ContentSection>
	);
}

function JobSubmissionInstructionsModal({ onClose }) {
	const [copied, setCopied] = React.useState(false);

	const handleCopyEmail = React.useCallback((event) => {
		event.preventDefault();
		navigator.clipboard.writeText("promotejobs@dproz.com");
		setCopied(true);
		window.setTimeout(() => setCopied(false), 2000);
	}, []);

	return (
		<Modal title="Ready to find your next hire?" onClose={onClose} width="max-w-4xl">
			<div>
				<div className="mx-auto max-w-xl px-2 py-4 text-center sm:px-4 sm:py-6">
					<p className="mx-auto mb-8 max-w-md font-inter text-base leading-relaxed text-muted-foreground sm:text-lg">
						Send us the job description and we'll publish and promote it to thousands of candidates.{" "}
						<Link to="/for-employers" className="font-medium text-primary-700 hover:text-primary-800 cursor-pointer">
							Learn more ...
						</Link>
						.
					</p>
					<div className="flex flex-col items-center gap-4">
						<a
							href="mailto:promotejobs@dproz.com?subject=Job%20Posting%20Request"
							className="inline-flex items-center gap-2.5 rounded-full bg-primary-700 px-7 py-3.5 font-inter text-[15px] font-semibold text-white transition-colors hover:bg-primary-800"
						>
							<Mail className="h-[18px] w-[18px]" />
							Send a Job via Email
							<ArrowRight className="h-4 w-4" />
						</a>
						<button
							type="button"
							onClick={handleCopyEmail}
							className="inline-flex items-center gap-2 font-inter text-sm text-muted-foreground transition-colors hover:text-foreground"
						>
							<span>promotejobs@dproz.com</span>
							{copied ? <Check className="h-3.5 w-3.5 text-green-500" /> : <Copy className="h-3.5 w-3.5" />}
						</button>
					</div>
				</div>

				<div className="-mx-6 mt-2 flex justify-end border-t border-border px-6 pt-4">
					<Button type="button" variant="outline" onClick={onClose}>
						Close
					</Button>
				</div>
			</div>
		</Modal>
	);
}

function ExtendDeadlineModal({ job, onClose, onSubmit, isSubmitting }) {
	const [newDeadline, setNewDeadline] = React.useState(job.deadline || dayjs().add(7, "day").format("YYYY-MM-DD"));

	return (
		<Modal title="Extend Deadline" onClose={onClose} width="max-w-sm">
			<form
				onSubmit={async (event) => {
					event.preventDefault();
					await onSubmit(job, newDeadline);
				}}
			>
				<p className="mb-3 text-sm text-muted-foreground">
					Set a new deadline for <span className="font-semibold text-foreground">{job.title}</span>.
				</p>
				<div className="mb-4">
					<label className="mb-1.5 block text-sm font-medium text-foreground">New Deadline</label>
					<input
						type="date"
						min={dayjs().format("YYYY-MM-DD")}
						value={newDeadline}
						disabled={isSubmitting}
						onChange={(event) => setNewDeadline(event.target.value)}
						className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm focus:outline-hidden focus:ring-2 focus:ring-ring disabled:opacity-60"
					/>
					<p className="mt-1.5 text-xs text-muted-foreground">
						Current deadline: {job.deadline ? dayjs(job.deadline).format("MMM D, YYYY") : "Not set"}
					</p>
				</div>
				<div className="mt-5 flex gap-3">
					<Button type="button" variant="outline" className="flex-1" disabled={isSubmitting} onClick={onClose}>
						Cancel
					</Button>
					<Button type="submit" className="flex-1" disabled={isSubmitting}>
						{isSubmitting ? "Updating..." : "Update Deadline"}
					</Button>
				</div>
			</form>
		</Modal>
	);
}

function MarkJobFilledModal({ job, onClose, onConfirm, isSubmitting }) {
	return (
		<Modal title="Mark as Filled" onClose={onClose} width="max-w-md">
			<div>
				<p className="text-sm text-muted-foreground">
					This will move <span className="font-semibold text-foreground">{job.title}</span> to the closed roles tab.
				</p>
				<div className="-mx-6 mt-5 flex gap-3 border-t border-border px-6 pt-4">
					<Button type="button" variant="outline" className="flex-1" disabled={isSubmitting} onClick={onClose}>
						Cancel
					</Button>
					<Button
						type="button"
						className="flex-1"
						disabled={isSubmitting}
						onClick={async () => {
							await onConfirm(job);
						}}
					>
						{isSubmitting ? "Updating..." : "Mark as Filled"}
					</Button>
				</div>
			</div>
		</Modal>
	);
}
